// Reward crates: the thing a Worms map drops in, and the reason to aim at something
// other than the other gorilla for one shot.
//
// One falls per round somewhere along the skyline, chosen by the same per-round dice
// as the city, so a shared match code rebuilds it in the same place. Either side can
// break it, and what is inside is a charge of a special — which, because a match
// keeps its charges from round to round, is something you carry forward rather than
// a bonus for the next ten seconds.
//
// It is deliberately not always there: a crate every round stops being a crate and
// becomes furniture.

import { ctx, W, H } from './canvas.js';
import { state } from './state.js';
import {
  FIELD_W,
  CPU_LEVELS,
  CRATE_ABSENT_PERCENT,
  CRATE_FALL_SPEED,
  CRATE_GRAB_R,
  CRATE_H,
  CRATE_LIFE_CHANCE,
  CRATE_PICKUP_H,
  CRATE_PICKUP_W,
  CRATE_SPAWN_Y,
  CRATE_W,
  CHARGE_CAP,
  LIFE_CAP,
} from './config.js';
import { matchRand } from './rng.js';
import { mods } from './modes.js';
import { record, savedCpuLevel } from './progress.js';

/** Where it is, or null when there is nothing to aim at. */
export function crateRect() {
  const c = state.crate;
  if (!c || c.taken) return null;

  return { x: c.x - CRATE_W / 2, y: c.y - CRATE_H, w: CRATE_W, h: CRATE_H };
}

/**
 * The highest roof under any part of the crate, or null when nothing is under it.
 *
 * Buildings abut exactly and a crate lands on a party wall as often as anywhere, so a crate is
 * regularly over two of them and the one it is standing on is the higher of the two — asked
 * here, when it lands and again on every frame it is settled, rather than remembered.
 */
function supportFor(c) {
  const left = c.x - CRATE_W / 2;
  const right = c.x + CRATE_W / 2;
  const under = state.buildings.filter(b => right > b.x && left < b.x + b.w);

  return under.reduce((best, b) => (best === null || b.y < best.y ? b : best), null);
}

/**
 * Decides this round's crate. Called at the end of makeCity, after everything else
 * that reads the generator, so it cannot shift the skyline of a code already shared.
 */
export function spawnCrate() {
  // Una ronda nueva es una caja nueva, y nadie la ha abierto todavía.
  state.crateTaken = false;

  // No specials in the mode, no loot: the crate could be shot but never broken, and
  // a crate you cannot open is worse than no crate. CLASSIC is the original.
  if (!mods().specials) {
    state.crate = null;
    return;
  }

  if (matchRand(1, 100) <= CRATE_ABSENT_PERCENT) {
    state.crate = null;
    return;
  }

  // Dentro del campo y no del mundo entero: una caja que cae en la ciudad de adorno no la
  // alcanza nadie, y la CPU se pasa el turno tirándole. Ver FIELD_W en config.js.
  const inset = (W - FIELD_W) / 2;

  state.crate = {
    x: matchRand(inset + 60, W - inset - 60),
    y: CRATE_SPAWN_Y,
    falling: true,
    taken: false,
    // Cuándo se posó, para el margen de la CPU: la cuenta empieza cuando el regalo está quieto y
    // a la vista, no cuando entra por arriba.
    settledAt: 0,
  };
}

/**
 * One frame of the drop, from the frame loop.
 *
 * Returns what was collected if the crate touched somebody on the way down, so the
 * caller can say so. A reward that lands on a gorilla is his: he does not have to
 * shoot his own present to open it.
 */
export function advanceCrate() {
  // Outside a round there is no crate at all, and it is not left hanging in the air
  // over the front end either, waiting for a match to happen behind it.
  if (!inRound()) {
    state.crate = null;
    return null;
  }

  const c = state.crate;
  if (!c) return null;

  // A settled crate is not asleep. Between two towers it is standing on a party wall, both
  // buildings shake in the earthquake, and which of their roofs is the higher one swaps as they
  // pass each other — so the roof it is on has to be asked again every frame rather than
  // remembered from the landing.
  //
  // Measured on the closest pair of roofs in a city (a 10px step between two neighbours), with
  // the crate anchored to the higher one the way landing does it: over two minutes of earthquake
  // the neighbour came up to leave it 25px below the higher roof. The crate is 18px tall, so for
  // a quarter of those frames it was a crate sitting inside a wall — invisible, and impossible to
  // shoot. That is the report: "between two buildings it rests on one or the other as it touches
  // them". It is the landing's own rule, asked again.
  if (!c.falling) {
    const on = supportFor(c);
    if (on) c.y = on.y;

    // Y la CPU va a por él. Contra la máquina el regalo es de quien lo coja primero, y esto es lo
    // que la CPU pone en la carrera: no pulsa nada, pero tampoco espera para siempre. El margen lo
    // dice la dificultad — la misma lista que dice cuánto tarda en apuntar — y el anillo que se
    // cierra alrededor del regalo es el aviso de que hay prisa: ver drawCrates().
    if (crateRaceOn() && state.clock - c.settledAt >= cpuGrabMs()) return collectCrate(1);

    return null;
  }

  c.y += CRATE_FALL_SPEED;

  // Asked before the landing check rather than after, because the interesting case
  // is a crate that comes down on somebody and then settles: checking after the
  // landing would return early and leave it sitting on his head, unopened.
  const hit = gorillaTouchedBy(c);
  if (hit >= 0) return collectCrate(hit);

  // What it lands on is the highest roof under ANY part of it, not the roof under
  // its middle.
  //
  // Buildings abut exactly and run from 105 to 330 tall, so a crate whose middle
  // comes down within its own half-width of a party wall used to rest on one roof
  // with the side of its body buried in the taller neighbour: a measured 13 crates
  // in a hundred, sitting half inside a wall, which is loot you cannot see and can
  // barely shoot. Falling until the highest surface under any part of it is solid
  // is what terrain does in this kind of game anyway — a crate lands on the ledge
  // and overhangs the drop, rather than sinking through it.
  const on = supportFor(c);

  if (on && c.y >= on.y) {
    c.y = on.y;
    c.falling = false;
    c.settledAt = state.clock;
    return;
  }

  // Or down the gap between two towers, in which case it is gone and no use to
  // anybody. A crate that piled up at the bottom of the screen for ever would be
  // worse than one that missed.
  if (c.y > H) state.crate = null;

  return null;
}

/**
 * Whether a round is being played right now.
 *
 * A crate belongs to a round and to nothing else. The front end builds a city the
 * same way a match does — `makeCity()` at boot, with `spawnCrate()` on the end of it
 * — so there was a real crate falling on the front end from the first frame, and the
 * gorillas it can land on are `state.players`, which are the very ones the demo shows.
 * A player watched a present come down on one of them and a reward arrive: measured,
 * the drop credited a charge to `state.specialUses` and toasted it, before anybody had
 * pressed anything. The crate is part of a round, so where there is no round there is
 * no crate.
 *
 * The pause is not a round in that sense either, and the loop does not step a crate in
 * it: a present that lands behind the overlay is one nobody watched come down, and its
 * reward announces itself over a match that is not moving.
 */
function inRound() {
  return state.screen === 'game';
}

/**
 * Which gorilla the falling crate is touching, or -1 for nobody.
 *
 * A player is stored by his feet, so his body is the box that stands on them. The
 * box is a little wider than he is, which is the point: a graze counts.
 */
function gorillaTouchedBy(c) {
  const r = crateRect();
  if (!r) return -1;

  for (let i = 0; i < state.players.length; i += 1) {
    const p = state.players[i];
    if (!p || !p.alive) continue;

    if (r.x + r.w > p.x - CRATE_PICKUP_W / 2 && r.x < p.x + CRATE_PICKUP_W / 2
      && r.y + r.h > p.y - CRATE_PICKUP_H && r.y < p.y) return i;
  }

  return -1;
}

/**
 * El toque del jugador sobre el regalo: ratón o dedo, el más rápido se lo lleva.
 *
 * Recibe coordenadas del campo y devuelve lo que había dentro, o null si no había nada que coger
 * — el mismo contrato que `advanceCrate`, para que quien llama lo anuncie igual.
 *
 * No cuesta el turno, y eso es una decisión y no un descuido: el regalo es lo único del juego que
 * se puede ir al otro lado sin gastar nada, y cobrarlo por turno convertiría "el que llegue antes"
 * en "el que no esté apuntando en ese momento", que es lo contrario de lo que el jugador pidió.
 * Un toque aquí tampoco dispara nada: el botón de lanzar es un botón.
 */
export function grabCrate(x, y, idx = state.turn) {
  const r = crateRect();
  if (!r) return null;

  const dx = x - (r.x + r.w / 2);
  const dy = y - (r.y + r.h / 2);
  if (dx * dx + dy * dy > CRATE_GRAB_R * CRATE_GRAB_R) return null;

  return collectCrate(idx);
}

/**
 * Cuánto falta para que la CPU se lleve el regalo, de 0 a 1, o null cuando no hay carrera.
 *
 * Lo que dibuja `drawCrates` es el aviso: un anillo que se cierra sobre el regalo mientras la
 * cuenta atrás corre. Sin él la CPU se lo llevaba a los tres segundos y el jugador no tenía forma
 * de saber que había prisa — una pérdida silenciosa, que es la peor clase.
 */
export function crateCountdown() {
  const c = state.crate;
  if (!c || c.taken || c.falling || !crateRaceOn()) return null;

  return Math.min(1, (state.clock - c.settledAt) / cpuGrabMs());
}

/** Si hay alguien compitiendo por el regalo que no sea un dedo. Hoy, la máquina. */
function crateRaceOn() {
  return state.cpuOn;
}

/** El margen que la CPU le da al jugador, leído de su dificultad. */
function cpuGrabMs() {
  const level = CPU_LEVELS.find(l => l.id === savedCpuLevel()) || CPU_LEVELS[1];
  return level.grabMs;
}

/**
 * Breaks it open for whoever's banana found it, and says what was inside.
 *
 * The reward is a charge of a special they already have. A charge of one they have
 * not earned yet would be invisible: ownership is derived from the achievements, so
 * there would be no button for it to live on. Handing over the unearned ones is the
 * better version of this and needs ownership to stop being derived — worth doing,
 * and not this.
 *
 * This is now the only thing that hands a charge back. It used to share that job with the
 * ulti meter — fill it by playing and every ult you own got a charge — and the meter is
 * gone: a crate is the way.
 */
export function collectCrate(idx = state.turn) {
  const c = state.crate;
  if (!c || c.taken) return null;

  c.taken = true;
  state.crate = null;
  // Y queda dicho para el código que se va a mandar: esta ronda ya no tiene caja que abrir.
  state.crateTaken = true;
  // For the achievement: a crate is the one reward in the game you have to go and get, so it is
  // worth knowing how many somebody has gone and got. And which somebody: con el regalo en
  // carrera lo abre quien llega antes, así que el que la coge no es siempre el del turno.
  record('crate', { side: idx });

  const owned = ownedSpecialIds(idx);

  // Una caja de cada cuatro trae una vida extra en vez de una carga: es la recompensa que pidió
  // el jugador —"que caigan vidas extra para aguantar ataques y no morir"— y solo sale si le
  // queda hueco, porque una vida que no cabe es una vida que se pierde: con el zurrón lleno
  // vuelve a salir una carga, como siempre.
  if (state.lives[idx] < LIFE_CAP && Math.random() < CRATE_LIFE_CHANCE) {
    state.lives[idx] += 1;
    return { kind: 'life', idx };
  }

  if (!owned.length) return null;

  const id = owned[Math.floor(Math.random() * owned.length)];
  // Credited to whoever broke it, or to whoever it landed on.
  const mine = state.specialUses[idx];
  mine[id] = Math.min(CHARGE_CAP, (mine[id] || 0) + 1);

  // El lado viaja con el premio, y no por elegancia: el aviso que lo anuncia tiene que decir si es
  // tuyo o de la CPU, y "tuyo también la ronda siguiente" dicho sobre una carga que se ha llevado
  // la máquina es una mentira de las que se notan. Ver announceLoot.
  return { kind: 'special', id, idx };
}

/**
 * The specials a crate is allowed to contain, read from the state rather than from
 * progress.js so this module does not have to import it: the ids the button is
 * already offering are exactly the ones with a place to live.
 */
function ownedSpecialIds(idx = state.turn) {
  return Object.keys(state.specialUses[idx] || {});
}

/**
 * A reward, drawn as a present: a wrapped box with a ribbon and a bow on top.
 *
 * It used to be a crate with strapping and a stencil on the side, which reads as
 * cargo — a fine thing for a level to contain and a poor thing for a reward to look
 * like. Nobody has to be told what a present with a bow is.
 */
export function drawCrates() {
  const r = crateRect();
  if (!r) return;

  const x = Math.round(r.x);
  const y = Math.round(r.y);
  const ribbon = '#ff4d6d';

  // The bow goes up into the sky above the box, which is why it is drawn first: the
  // lid then covers the place where the loops meet it.
  ctx.fillStyle = ribbon;
  [-1, 1].forEach(dir => {
    ctx.beginPath();
    ctx.ellipse(x + r.w / 2 + dir * 4.6, y - 3.2, 4.4, 3.1, dir * 0.5, 0, Math.PI * 2);
    ctx.fill();
  });

  // The box, gold because gold is what this game uses for the thing worth having.
  ctx.fillStyle = '#e0a63c';
  ctx.fillRect(x, y, r.w, r.h);
  // Its lid a shade lighter, which is most of what makes a box read as wrapped
  // rather than as a box.
  ctx.fillStyle = '#f5c65c';
  ctx.fillRect(x, y, r.w, 6);

  // The ribbon, one band each way, crossing on the lid.
  ctx.fillStyle = ribbon;
  ctx.fillRect(x + r.w / 2 - 2.5, y, 5, r.h);
  ctx.fillRect(x, y + r.h / 2 - 2.5, r.w, 5);

  // And the knot, over the middle of the bow.
  ctx.beginPath();
  ctx.arc(x + r.w / 2, y - 1.4, 2.7, 0, Math.PI * 2);
  ctx.fill();

  // And a shadow on the roof, so a crate in mid-air is visibly in mid-air.
  ctx.fillStyle = 'rgba(8,11,21,0.55)';
  ctx.fillRect(x + 2, Math.round(r.y + r.h), r.w - 4, 3);

  // Y el aviso de la carrera, si la hay: un anillo sobre el regalo que se cierra mientras la CPU
  // se acerca a por él. El aro entero va primero y muy tenue, para que el arco se lea como lo que
  // queda y no como un adorno suelto.
  const k = crateCountdown();
  if (k === null) return;

  const cx2 = x + r.w / 2;
  const cy2 = Math.round(r.y) + r.h / 2;

  ctx.beginPath();
  ctx.arc(cx2, cy2, 20, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255, 210, 74, 0.16)';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx2, cy2, 20, -Math.PI / 2, -Math.PI / 2 + k * Math.PI * 2);
  ctx.strokeStyle = 'rgba(255, 210, 74, 0.9)';
  ctx.lineWidth = 2.5;
  ctx.stroke();
}
